import { add, divide, subtract } from 'tio-ui/math';
import { concat } from '@ember/helper';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { not, or } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import { VStack } from 'tio-reimagined/components/layout';
import {
  PhCheckCircle,
  PhCurrencyCircleDollar,
  PhCurrencyDollar,
  PhGraduationCap,
  PhReceipt,
} from 'ember-phosphor-icons';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import CalculatedApplicationTotal from '../../tuition-assistance/application-details/calculated-application-total.gts';
import EditableApplicationTotal from '../../tuition-assistance/application-details/editable-application-total.gts';
import ReimbursementCalculationIfApplicable from '../../tuition-assistance/reimbursement-calculation-if-applicable.gts';
import safeFormatNumber from '../../../helpers/safe-format-number.ts';
import type { TOC } from '@ember/component/template-only';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';

interface FinancialDetailsApprovalSummarySig {
  Args: {
    application: TasApplicationModel;
    programInstance: TasProgramInstanceModel;
    isCalculatedApplicationTotal: boolean;
    readonlyOriginalApplicationTotal: number;
    readonlyCourseCashTuitionTotal?: number;
    updateAndSaveApprovedTotalWithComment: (
      approvedTotal: number,
      comment: string,
      approvedCourseCashTotalField?: Record<string, unknown>
    ) => void;
    setApplicationTotal: (approvedTotal: number) => void;
    setCourseCashTotal: (courseCashTotal: number) => void;
    isProhibitedEdit: boolean;
    isPendingApprovalAndNotReadonly: boolean;
    isAllowedEditApprovedTotalIfNotPendingApproval: boolean;
    isSubmitting: boolean;
  };
  Blocks: {
    warning: [];
  };
  Element: HTMLDivElement;
}

/**
 * Financial Details Approval Summary Component
 *
 * Styled to match the Figma design (similar to FinancialDetailsRequestSummary)
 * but shows the pre-payment approval summary with:
 * - Course breakdown cards
 * - Scholarships (if applicable)
 * - Requested reimbursement with reimbursement calculation
 * - Approved total with editing capability (uses CalculatedApplicationTotal or EditableApplicationTotal)
 */
const FinancialDetailsApprovalSummary: TOC<FinancialDetailsApprovalSummarySig> = <template>
  <VStack class="tio-col-flush w-full h-full" ...attributes>
    <div class="tio-col-extra-loose flex-1">
      {{! ========================================
            REQUEST SUMMARY Section - Card-based layout
            ======================================== }}
      <section aria-labelledby="courses-section-heading">
        <Heading
          @level={{4}}
          class="text-sm font-semibold tio-fg-muted uppercase tracking-wider mb-3"
        >
          {{t "tuition_assistance.application_details.approval_summary"}}
        </Heading>

        <div class="tio-col" role="list">
          {{! Course Cards }}
          {{#each @application.activeCourses as |course index|}}
            <div
              class="app-card rounded-xl py-4 pl-4 pr-4"
              data-test-course-item={{index}}
              role="listitem"
            >
              <div class="flex items-center justify-between gap-4">
                {{! Left Frame - Icon and Course Name }}
                <div class="tio-row-loose flex-1 min-w-0">
                  <div class="shrink-0 tio-bg-sunken rounded-full p-1">
                    <PhGraduationCap @size={{20}} class="tio-fg-secondary" />
                  </div>
                  <span class="font-medium text-base tio-fg-primary truncate">
                    {{or
                      (or course.courseName course.courseNumber)
                      (concat "Expense " (add index 1))
                    }}
                  </span>
                </div>

                {{! Right Frame - Amount }}
                <span class="font-semibold text-base tio-fg-secondary tabular-nums">
                  {{safeFormatNumber
                    (divide course.courseTotal 100)
                    style="currency"
                    currency="USD"
                  }}
                </span>
              </div>
            </div>
          {{/each}}

          {{! Scholarships Card }}
          {{#if @application.includesScholarships}}
            <div
              class="app-status-panel app-status-panel--warning rounded-xl py-4 pl-4 pr-4"
              data-test-scholarships
              role="listitem"
            >
              <div class="flex items-center justify-between gap-4">
                <div class="tio-row-loose flex-1 min-w-0">
                  <div class="app-icon-circle--warning shrink-0 rounded-full p-1">
                    <PhReceipt @size={{20}} class="app-icon-warning" />
                  </div>
                  <span class="font-medium text-base app-status-text-warning">
                    {{t "tuition_assistance.application_details.scholarships_received"}}
                  </span>
                </div>
                <span class="font-semibold text-base app-status-text-warning tabular-nums">
                  {{safeFormatNumber
                    (divide (subtract 0 @application.scholarshipsTotal) 100)
                    style="currency"
                    currency="USD"
                  }}
                </span>
              </div>
            </div>
          {{/if}}
        </div>
      </section>

      {{! ========================================
            TOTALS Section
            ======================================== }}
      <section aria-labelledby="totals-section-heading">
        <Heading
          @level={{4}}
          class="text-sm font-semibold tio-fg-muted uppercase tracking-wider mb-3"
        >
          {{t "common.tas.approval.request_summary.sections.totals"}}
        </Heading>

        <div class="tio-col" role="list">
          {{! Requested Reimbursement Card }}
          <div class="app-card rounded-xl py-4 pl-4 pr-4" data-test-requested-total role="listitem">
            <div class="flex items-center justify-between gap-4">
              <div class="tio-row-loose flex-1 min-w-0">
                <div class="shrink-0 tio-bg-sunken rounded-full p-1">
                  <PhCurrencyDollar @size={{20}} class="tio-fg-secondary" />
                </div>
                <span class="font-medium text-base tio-fg-primary">
                  {{t "tuition_assistance.application_details.requested_reimbursement"}}
                </span>
              </div>
              <span class="font-semibold text-base tio-fg-secondary tabular-nums">
                {{safeFormatNumber
                  (divide @application.requestedTotal 100)
                  style="currency"
                  currency="USD"
                }}
              </span>
            </div>
          </div>

          {{! Reimbursement Calculation Info }}
          <div class="px-4">
            <ReimbursementCalculationIfApplicable @application={{@application}} />
            {{#if @application.canRequestRepayment}}
              <div class="my-2">
                <AlertBanner @intent="info">
                  <p>
                    {{t "tuition_assistance.application_details.partial_reimbursement_instruction"}}
                  </p>
                </AlertBanner>
              </div>
            {{/if}}
          </div>

          {{! Approved Total or Empty State }}
          {{#if @application.shouldDisplayApprovedTotal}}
            {{! Approved Total Card - Primary/highlighted }}
            <div
              class="border border-teal-200 dark:border-teal-900/60 bg-teal-50 dark:bg-teal-950/40 rounded-xl py-4 px-4"
              data-test-approved-total
              role="listitem"
            >
              <div class="flex items-center justify-between gap-4">
                {{! Left Frame - Icon and Label }}
                <div class="tio-row-loose flex-1 min-w-0">
                  <div class="shrink-0 bg-teal-100 dark:bg-teal-900/40 rounded-full p-1">
                    <PhCheckCircle @size={{20}} class="text-teal-600 dark:text-teal-200" />
                  </div>
                  <span class="font-medium text-base text-teal-900 dark:text-teal-200">
                    {{t "tuition_assistance.program_details.status.approved_total"}}
                  </span>
                </div>

                {{! Right Frame - Amount and Edit }}
                <div class="tio-row-tight">
                  {{#if @isCalculatedApplicationTotal}}
                    {{! Calculated totals - uses modal with percentage breakdown }}
                    <CalculatedApplicationTotal
                      @approvedTotal={{@readonlyOriginalApplicationTotal}}
                      @application={{@application}}
                      @tasProgramTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
                      @onSave={{@updateAndSaveApprovedTotalWithComment}}
                      @canEdit={{not @isProhibitedEdit}}
                      @courseCashTotal={{@readonlyCourseCashTuitionTotal}}
                    />
                  {{else if @isAllowedEditApprovedTotalIfNotPendingApproval}}
                    {{! Non-pending but editable - uses popover/modal }}
                    <EditableApplicationTotal
                      @approvedTotal={{@readonlyOriginalApplicationTotal}}
                      @onSave={{@updateAndSaveApprovedTotalWithComment}}
                      @application={{@application}}
                      @courseCashTotal={{@readonlyCourseCashTuitionTotal}}
                    />
                  {{else}}
                    {{! Read-only display of approved total }}
                    <span class="tio-text-heading text-teal-700 dark:text-teal-200 tabular-nums">
                      {{safeFormatNumber
                        (divide @readonlyOriginalApplicationTotal 100)
                        style="currency"
                        currency="USD"
                      }}
                    </span>
                  {{/if}}
                </div>
              </div>
            </div>
          {{else}}
            {{! Before first approval (pre-attend states) - show empty state }}
            <div
              class="tio-place-center p-8 bg-gray-50 rounded-lg border tio-border-default"
              role="status"
              data-test="financial-details-empty-state"
            >
              <PhCurrencyCircleDollar @size="48" class="tio-fg-disabled mb-4" />
              <Heading @level={{5}} class="tio-fg-secondary mb-2">
                {{t "common.tas.approval.financial_details.empty_state.title"}}
              </Heading>
              <Text @variant="muted" @size="sm" class="text-center max-w-xs">
                {{t "common.tas.approval.financial_details.empty_state.description"}}
              </Text>
            </div>
          {{/if}}
        </div>
      </section>
    </div>

    {{! Warning slot for limits alerts }}
    {{yield to="warning"}}
  </VStack>
</template>;

export default FinancialDetailsApprovalSummary;
